import React, { useState, useEffect, useCallback } from 'react';
import { Link2, Key, RefreshCw, CheckCircle, XCircle, Loader2, Clock, ArrowRightLeft } from 'lucide-react';
import api from '../services/api';
import { useToast } from '../components/Toast';

export default function IntegrationsPage() {
  const toast = useToast();
  const [settings, setSettings] = useState({});
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);

  // Form state
  const [nprUrl, setNprUrl] = useState('');
  const [nprApiKey, setNprApiKey] = useState('');
  const [swibiUrl, setSwibiUrl] = useState('');

  const load = useCallback(async () => {
    try {
      const [settingsRes, logsRes] = await Promise.all([
        api.get('/integration/settings'),
        api.get('/integration/logs')
      ]);
      const s = settingsRes.data.settings;
      setSettings(s);
      setNprUrl(s.npr_url || '');
      setNprApiKey(s.npr_api_key || '');
      setSwibiUrl(s.swibi_url || '');
      setLogs(logsRes.data.logs);
    } catch (err) {
      toast.error('Błąd ładowania ustawień');
    } finally {
      setLoading(false);
    }
  }, [toast]);

  useEffect(() => { load(); }, [load]);

  const saveSetting = async (key, value) => {
    try {
      await api.put('/integration/settings', { key, value });
      toast.success('Zapisano');
      setSettings(prev => ({ ...prev, [key]: value }));
    } catch {
      toast.error('Błąd zapisu');
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const r = await api.post('/integration/test-connection');
      setTestResult(r.data);
    } catch (err) {
      setTestResult({ ok: false, message: err.response?.data?.error || 'Błąd połączenia' });
    } finally {
      setTesting(false);
    }
  };

  if (loading) return <div className="p-6 text-gray-400">Ładowanie...</div>;

  return (
    <div className="p-6 max-w-4xl">
      <h1 className="text-2xl font-bold text-gray-800 flex items-center gap-3 mb-6">
        <ArrowRightLeft size={28} className="text-indigo-600" /> Integracje
      </h1>

      {/* NPR Connection */}
      <div className="bg-white border border-gray-200 rounded-lg p-6 mb-6">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <Link2 size={20} className="text-emerald-600" /> Połączenie z NPR
        </h2>
        <p className="text-sm text-gray-500 mb-4">
          Konfiguracja połączenia z systemem NPR. Po zaakceptowaniu oferty dane firmy i montażu zostaną automatycznie przeniesione.
        </p>

        <div className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL systemu NPR</label>
            <div className="flex gap-2">
              <input type="url" value={nprUrl} onChange={e => setNprUrl(e.target.value)}
                placeholder="https://npr.eskada.pl"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <button onClick={() => saveSetting('npr_url', nprUrl)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
                Zapisz
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Klucz API NPR</label>
            <div className="flex gap-2">
              <input type="text" value={nprApiKey} onChange={e => setNprApiKey(e.target.value)}
                placeholder="Wklej klucz API wygenerowany w NPR"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm font-mono" />
              <button onClick={() => saveSetting('npr_api_key', nprApiKey)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
                Zapisz
              </button>
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">URL tego systemu (SWIBI API) — do przesyłania plików</label>
            <div className="flex gap-2">
              <input type="url" value={swibiUrl} onChange={e => setSwibiUrl(e.target.value)}
                placeholder="https://api.swibi.net"
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm" />
              <button onClick={() => saveSetting('swibi_url', swibiUrl)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg text-sm">
                Zapisz
              </button>
            </div>
          </div>

          <div className="flex items-center gap-3 pt-2">
            <button onClick={testConnection} disabled={testing || !nprUrl || !nprApiKey}
              className="flex items-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-50 text-white rounded-lg text-sm">
              {testing ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
              {testing ? 'Testuję...' : 'Test połączenia'}
            </button>
            {testResult && (
              <span className={`flex items-center gap-1 text-sm ${testResult.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                {testResult.ok ? <CheckCircle size={16} /> : <XCircle size={16} />}
                {testResult.message}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Integration Logs */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h2 className="text-lg font-semibold text-gray-800 flex items-center gap-2 mb-4">
          <Clock size={20} className="text-gray-500" /> Logi integracji
        </h2>
        {logs.length === 0 ? (
          <p className="text-sm text-gray-400">Brak logów</p>
        ) : (
          <div className="space-y-2 max-h-96 overflow-y-auto">
            {logs.map(log => (
              <div key={log.id} className={`flex items-start gap-3 p-3 rounded-lg text-sm ${log.status === 'error' ? 'bg-red-50 border border-red-200' : 'bg-gray-50 border border-gray-200'}`}>
                <div className={`mt-0.5 ${log.status === 'error' ? 'text-red-500' : 'text-emerald-500'}`}>
                  {log.status === 'error' ? <XCircle size={16} /> : <CheckCircle size={16} />}
                </div>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium text-gray-800">{log.action}</span>
                    <span className="text-xs text-gray-400">{log.target_system}</span>
                    <span className="text-xs text-gray-400">{new Date(log.created_at).toLocaleString('pl-PL')}</span>
                  </div>
                  {log.error_message && <div className="text-red-600 text-xs mt-1">{log.error_message}</div>}
                  {log.payload && (
                    <div className="text-xs text-gray-500 mt-1">
                      {typeof log.payload === 'string' ? log.payload : JSON.stringify(log.payload)}
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
